import { haversineKm, type LatLng } from './haversine';
import { calcTransportCost } from './calcTransportCost';
import { variantPricePerPerson } from './priceForParticipants';
import { variantPriceSpan } from '@funkey/shared/activities';
import type { PartnerOption } from '../data/getGroupPartners';

export type PartnerBadge = 'recommended' | 'closest' | 'cheapest';

export type RankedPartner = {
    partner: PartnerOption;
    badges: ReadonlyArray<PartnerBadge>;
    /** Straight-line km from the user's location to the closest address. Null when no location. */
    distanceKm: number | null;
    /**
     * The address `distanceKm` was measured to. The card labels itself with this venue's town,
     * not the partner's head office: a Mouscron partner with a venue in Antwerp read
     * "MOUSCRON — ≈ 2 km van Antwerpen", two facts that cannot both be about the same place.
     */
    closestAddress: PartnerOption['addresses'][number] | null;
    /** Cheapest variant price per-person at given participants. */
    cheapestPerPerson: number;
};

/**
 * Distance to the nearest address of this partner.
 *
 * `venuesOnly` excludes the synthetic partner-HQ point that `getGroupPartners` pushes for
 * partners without real `partner_address` rows. On the "Ik ga naar de aanbieder" tab you travel
 * to a venue, and the HQ is not one — measuring to it ranked a partner as nearby and then sent
 * the user to a venue much farther away.
 */
function closestAddress(
    partner: PartnerOption,
    userLocation: LatLng,
    venuesOnly: boolean,
): { address: PartnerOption['addresses'][number]; distanceKm: number } | null {
    let best: { address: PartnerOption['addresses'][number]; distanceKm: number } | null = null;
    for (const address of partner.addresses) {
        if (venuesOnly && address.isHeadquarters) continue;
        if (address.lat == null || address.lng == null) continue;
        const distanceKm = haversineKm(userLocation, { lat: address.lat, lng: address.lng });
        if (!best || distanceKm < best.distanceKm) best = { address, distanceKm };
    }
    return best;
}

/** The address that represents this partner when no location is known: its first venue. */
function fallbackAddress(
    partner: PartnerOption,
    venuesOnly: boolean,
): PartnerOption['addresses'][number] | null {
    return (
        partner.addresses.find(
            address => !(venuesOnly && address.isHeadquarters) && address.lat != null,
        ) ?? null
    );
}

function cheapestPerPersonAt(participants: number, partner: PartnerOption): number {
    return (
        variantPriceSpan(
            partner.variants.map(variant =>
                variantPricePerPerson(variant, participants, partner.priceType),
            ),
        )?.min ?? 0
    );
}

/**
 * Rank partners, attaching badges based on:
 * - `recommended`  → partner.isRecommended === true
 * - `closest`      → partner with the smallest straight-line distance (requires userLocation)
 * - `cheapest`     → partner with the lowest variant price per-person at the given participants count,
 *                    including the partner's transport cost when a location is known
 *
 * If the same partner wins all three criteria, only that partner is returned
 * and the caller should hide the "show more" / "collapse" controls.
 */
export function rankPartners(args: {
    partners: ReadonlyArray<PartnerOption>;
    participants: number;
    userLocation: LatLng | null;
    /**
     * True on the "Ik ga naar de aanbieder" tab: distances must be to bookable venues only,
     * never the partner's head office.
     */
    venuesOnly?: boolean;
}): ReadonlyArray<RankedPartner> {
    const { partners, participants, userLocation } = args;
    const venuesOnly = args.venuesOnly === true;

    const enriched: RankedPartner[] = partners.map(partner => {
        const closest = userLocation ? closestAddress(partner, userLocation, venuesOnly) : null;
        return {
            partner,
            badges: [],
            distanceKm: closest?.distanceKm ?? null,
            closestAddress: closest?.address ?? fallbackAddress(partner, venuesOnly),
            cheapestPerPerson: cheapestPerPersonAt(participants, partner),
        };
    });

    // Recommended: derived server-side in `getGroupPartners` (preferred partner, else the
    // group's primary one). Deliberately no fallback — filtering for the active tab can drop
    // the flagged partner from this list, and badging whoever happened to come first then put
    // "Aanbevolen" on a partner nobody recommended. No recommendation is better than a wrong
    // one; the badge means something or it is absent.
    const recommended = enriched.find(r => r.partner.isRecommended);
    if (recommended) {
        recommended.badges = [...recommended.badges, 'recommended'];
    }

    if (userLocation) {
        // Closest — stable tie-break on equal distance (same km → lowest activityId).
        let closest: RankedPartner | null = null;
        for (const r of enriched) {
            if (r.distanceKm == null) continue;
            const d = r.distanceKm;
            const bestD = closest?.distanceKm ?? Infinity;
            const isCloser = !closest || d < bestD;
            const isTieBreakCloser =
                closest != null && d === bestD && r.partner.activityId < closest.partner.activityId;
            if (isCloser || isTieBreakCloser) closest = r;
        }
        if (closest) closest.badges = [...closest.badges, 'closest'];

        // Cheapest including transport — stable tie-break on equal total (lowest activityId wins).
        let cheapest: RankedPartner | null = null;
        let cheapestTotal = Infinity;
        for (const r of enriched) {
            const transport = calcTransportCost({
                distanceKm: r.distanceKm,
                tiers: r.partner.transportCostTiers,
                transportMultiplier: r.partner.transportMultiplier,
                participants,
            });
            const total = r.cheapestPerPerson * participants + transport.costEUR;
            const isCheaper = total < cheapestTotal;
            const isTieBreakCheaper =
                total === cheapestTotal &&
                cheapest != null &&
                r.partner.activityId < cheapest.partner.activityId;
            if (isCheaper || isTieBreakCheaper) {
                cheapestTotal = total;
                cheapest = r;
            }
        }
        if (cheapest) cheapest.badges = [...cheapest.badges, 'cheapest'];
    }

    return enriched;
}
